import { roundTo } from '../units/round.js';

export interface BodyComposition {
  weightKg: number;
  /** Porcentaje de grasa estimado (bioimpedancia). Señal secundaria. */
  bodyFatPct: number | null;
}

/** Masa magra estimada. `null` si no hay estimacion de grasa. */
export function leanBodyMassKg(body: BodyComposition): number | null {
  if (body.bodyFatPct === null) return null;
  if (body.bodyFatPct < 1 || body.bodyFatPct > 70) return null;
  return roundTo(body.weightKg * (1 - body.bodyFatPct / 100), 2);
}

/** Ratio cintura/altura: señal de composicion mas fiable que la bioimpedancia sola. */
export function waistToHeightRatio(waistCm: number, heightCm: number): number {
  if (heightCm <= 0) throw new Error('Altura invalida');
  return roundTo(waistCm / heightCm, 3);
}

/**
 * Como se ha llegado al numero, no que formula se prefirio.
 *
 * `blended` no es una formula: es la media de las otras dos, y existe porque
 * cuando divergen mas del 10 % el resultado deja de ser Katch-McArdle. Se
 * declaraba `katch_mcardle` igualmente, asi que el `¿Por que?` y la fila
 * guardada nombraban una formula que no era la que habia producido el numero
 * (NUTR-002). El valor no cambia; lo que cambia es que ahora se llama por su
 * nombre.
 */
export type RmrMethod = 'katch_mcardle' | 'mifflin_st_jeor' | 'blended';

export interface RmrEstimate {
  kcal: number;
  method: RmrMethod;
  /** true si las dos formulas difieren mas de un 10 % (docs/NUTRITION_ENGINE.md §2.1). */
  lowConfidence: boolean;
}

export class MissingProfileDataError extends Error {
  constructor(readonly missing: string) {
    super(`Faltan datos para estimar el gasto en reposo: ${missing}`);
    this.name = 'MissingProfileDataError';
  }
}

/** Katch-McArdle: usa masa magra, no necesita la edad. */
export function rmrKatchMcArdle(leanMassKg: number): number {
  return roundTo(370 + 21.6 * leanMassKg, 0);
}

/** Mifflin-St Jeor: requiere edad. */
export function rmrMifflinStJeor(input: {
  weightKg: number;
  heightCm: number;
  ageYears: number;
  sex: 'male' | 'female';
}): number {
  const base = 10 * input.weightKg + 6.25 * input.heightCm - 5 * input.ageYears;
  return roundTo(input.sex === 'male' ? base + 5 : base - 161, 0);
}

/**
 * Estimacion de gasto en reposo. Prioriza Katch-McArdle porque el perfil puede
 * no incluir la edad (docs/RISKS.md R-03). Nunca inventa datos: si no hay
 * informacion suficiente, lanza un error tipado.
 */
export function estimateRmr(input: {
  weightKg: number;
  heightCm: number;
  bodyFatPct: number | null;
  ageYears: number | null;
  sex: 'male' | 'female';
}): RmrEstimate {
  const lean = leanBodyMassKg({ weightKg: input.weightKg, bodyFatPct: input.bodyFatPct });

  if (lean !== null) {
    const katch = rmrKatchMcArdle(lean);
    if (input.ageYears === null) {
      return { kcal: katch, method: 'katch_mcardle', lowConfidence: false };
    }
    const mifflin = rmrMifflinStJeor({
      weightKg: input.weightKg,
      heightCm: input.heightCm,
      ageYears: input.ageYears,
      sex: input.sex,
    });
    const divergence = Math.abs(katch - mifflin) / Math.min(katch, mifflin);

    // El umbral, el redondeo y `lowConfidence` se quedan exactamente como
    // estaban: lo unico que se corrige es la ETIQUETA. Una media de 1212 y 1345
    // da 1279, que no es lo que devuelve Katch-McArdle para nadie.
    if (divergence > 0.1) {
      return { kcal: roundTo((katch + mifflin) / 2, 0), method: 'blended', lowConfidence: true };
    }
    return { kcal: katch, method: 'katch_mcardle', lowConfidence: false };
  }

  if (input.ageYears === null) {
    throw new MissingProfileDataError('grasa corporal o edad');
  }

  return {
    kcal: rmrMifflinStJeor({
      weightKg: input.weightKg,
      heightCm: input.heightCm,
      ageYears: input.ageYears,
      sex: input.sex,
    }),
    method: 'mifflin_st_jeor',
    lowConfidence: false,
  };
}
